import React, { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import Map, { Marker, Source, Layer } from 'react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {
  Box,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
} from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import SaveIcon from '@mui/icons-material/Save'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import { drivingAreaPath } from '../utils/drivingArea'
import DrivingDistanceField from '../components/DeliveryBoundaries/DrivingDistanceField'

import {
  restaurantList,
  getRestaurantDeliveryZones,
  saveDeliveryZones,
  allRestaurantDeliverySummary,
} from '../apollo'
import { transformPolygon, transformPath } from '../utils/coordinates'
import ConfigurableValues from '../config/constants'
import { ZONE_COLORS } from '../components/DeliveryBoundaries/ZoneEditor'
import PlatformDistancePricingEditor from '../components/DeliveryBoundaries/PlatformDistancePricingEditor'
import TruckDeliveryToday from '../components/DeliveryBoundaries/TruckDeliveryToday'
import DrawControl from '../components/MapDraw/DrawControl'
import { clockLabel } from '../components/SelfDeliverySchedule/SelfDeliveryPanel'

const RESTAURANT_LIST = gql`${restaurantList}`
const GET_ZONES = gql`${getRestaurantDeliveryZones}`
const SAVE_ZONES = gql`${saveDeliveryZones}`
const GET_SUMMARY = gql`${allRestaurantDeliverySummary}`

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' }

// High-contrast styles for mapbox-gl-draw so the boundary being drawn/edited is
// clearly visible (the default faint blue is nearly invisible over a map).
const DRAW_STYLES = [
  { id: 'gl-draw-polygon-fill', type: 'fill', filter: ['all', ['==', '$type', 'Polygon']], paint: { 'fill-color': '#6366f1', 'fill-opacity': 0.25 } },
  { id: 'gl-draw-polygon-stroke', type: 'line', filter: ['all', ['==', '$type', 'Polygon']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#4f46e5', 'line-width': 3 } },
  { id: 'gl-draw-line', type: 'line', filter: ['all', ['==', '$type', 'LineString']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#4f46e5', 'line-width': 3 } },
  { id: 'gl-draw-vertex-halo', type: 'circle', filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']], paint: { 'circle-radius': 7, 'circle-color': '#fff' } },
  { id: 'gl-draw-vertex', type: 'circle', filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']], paint: { 'circle-radius': 5, 'circle-color': '#4f46e5' } },
]

// Self Delivery on a schedule ("every day from 2 PM", "only today", or "until
// I turn it off" from a later time) still leaves part of the day to RushMyFood,
// so the restaurant needs a boundary here as well as its own zones. Mirrors
// usesPlatformToo in the backend's selfDeliverySchedule.js.
const selfDeliveryAllDay = r =>
  r.selfDelivery === true && (!r.selfDeliveryMode || r.selfDeliveryMode === 'ALWAYS') && !r.selfDeliveryStartsAt

// When a part-day restaurant's own drivers take over.
function ownDriversNote(r) {
  const from = r.selfDeliveryFromMinute != null ? ` from ${clockLabel(r.selfDeliveryFromMinute)}` : ''
  if (r.selfDeliveryMode === 'DAILY') return `Own drivers every day${from}`
  if (r.selfDeliveryMode === 'TODAY') return `Own drivers today${from}`
  return `Own drivers${from} until turned off`
}

// A food truck's drawn boundary is never read: it delivers a radius around
// wherever each truck is parked (isWithinStopRadius in the backend).
function truckRadiusStatus(r) {
  const miles = parseFloat(r.mobileDeliveryRadiusMiles)
  if (!r.mobileDeliveryEnabled) return { ok: null, text: 'Pickup only' }
  if (!(miles > 0)) return { ok: false, text: 'No radius — no delivery' }
  return { ok: true, text: `${miles} mi around each stop` }
}

function polygonToPath(polygon) {
  if (!polygon?.coordinates?.[0]) return []
  return transformPolygon(polygon.coordinates[0])
}

function pathToPolygonInput(path) {
  return { coordinates: transformPath(path) }
}

// Closed GeoJSON polygon Feature from a [{lat,lng}] path (first==last point).
function pathToGeoJsonFeature(path) {
  if (!path || path.length < 3) return null
  const ring = path.map(p => [p.lng, p.lat])
  ring.push([path[0].lng, path[0].lat])
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } }
}

// mapbox-gl-draw polygon Feature -> [{lat,lng}] path (drops the closing point).
function drawFeatureToPath(feature) {
  const ring = feature?.geometry?.coordinates?.[0]
  if (!ring || ring.length < 4) return []
  return ring.slice(0, -1).map(([lng, lat]) => ({ lat, lng }))
}

export default function DeliveryBoundaries() {
  const { MAPBOX_TOKEN } = ConfigurableValues()

  const [tab, setTab] = useState(0) // 0 = Fee, 1 = Boundaries, 2 = Self-delivery

  const [selectedRestaurantId, setSelectedRestaurantId] = useState('')
  const [selectedRestaurant, setSelectedRestaurant] = useState(null)
  const [zones, setZones] = useState([])
  const [selectedZoneIndex, setSelectedZoneIndex] = useState(null)
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.006 })
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  // Boundary-area modal (draw or distance-radius only — no fees)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('add')
  const [editingZoneIndex, setEditingZoneIndex] = useState(null)
  const [modalMethod, setModalMethod] = useState('draw') // 'draw' | 'distance'
  // Held as typed, so a half-finished "2." doesn't snap back mid-keystroke.
  const [modalDistanceMiles, setModalDistanceMiles] = useState('5')
  const [modalError, setModalError] = useState('')
  // Building a driving area is a Mapbox round trip, so the button waits on it.
  const [modalBusy, setModalBusy] = useState(false)

  const mapRef = useRef(null)
  const drawRef = useRef(null)
  const [drawActive, setDrawActive] = useState(false)

  // ── Queries ────────────────────────────────────────────────────────────
  const { data: restaurantData, loading: restaurantsLoading } = useQuery(RESTAURANT_LIST)
  const { data: summaryData, loading: summaryLoading, refetch: refetchSummary } = useQuery(GET_SUMMARY, {
    fetchPolicy: 'network-only',
  })

  const { loading: zonesLoading, refetch: refetchZones } = useQuery(GET_ZONES, {
    variables: { restaurantId: selectedRestaurantId },
    skip: !selectedRestaurantId || !!selectedRestaurant?.isMobile,
    onCompleted: (data) => {
      const loaded = (data?.restaurantDeliveryZones || []).map(z => ({
        _id: z._id,
        name: z.name,
        fee: '0',
        sortOrder: z.sortOrder ?? 0,
        polygon: z.polygon,
        _path: polygonToPath(z.polygon),
      }))
      setZones(loaded)
      setSelectedZoneIndex(loaded.length > 0 ? 0 : null)
    },
  })

  const [mutSaveZones, { loading: saving }] = useMutation(SAVE_ZONES, {
    onCompleted: () => {
      setSuccessMsg('Delivery boundary saved.')
      setErrorMsg('')
      refetchZones()
      refetchSummary()
      setTimeout(() => setSuccessMsg(''), 4000)
    },
    onError: (err) => { setErrorMsg(err.message); setSuccessMsg('') },
  })

  // Reset boundary state when the selected restaurant changes.
  useEffect(() => {
    setZones([])
    setSelectedZoneIndex(null)
    setSuccessMsg('')
    setErrorMsg('')
    if (selectedRestaurant?.latitude && selectedRestaurant?.longitude) {
      setMapCenter({ lat: parseFloat(selectedRestaurant.latitude), lng: parseFloat(selectedRestaurant.longitude) })
    }
  }, [selectedRestaurantId, selectedRestaurant])

  // Fit the map to the boundary, or center on the restaurant.
  const fitToContent = React.useCallback(() => {
    const map = mapRef.current
    if (!map || drawActive) return
    const pts = []
    zones.forEach((z) => {
      if (Array.isArray(z._path)) z._path.forEach((p) => { if (p && p.lat != null && p.lng != null) pts.push([p.lng, p.lat]) })
    })
    if (pts.length >= 2) {
      const lngs = pts.map(p => p[0]); const lats = pts.map(p => p[1])
      map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 60, maxZoom: 15, duration: 400 })
      return
    }
    if (selectedRestaurant?.latitude && selectedRestaurant?.longitude) {
      map.easeTo({ center: [parseFloat(selectedRestaurant.longitude), parseFloat(selectedRestaurant.latitude)], zoom: 12, duration: 400 })
    }
  }, [zones, selectedRestaurant, drawActive])

  useEffect(() => { fitToContent() }, [fitToContent])

  // ── Restaurant picker ──────────────────────────────────────────────────
  const handleRestaurantChange = (id) => {
    setSelectedRestaurantId(id)
    const r = (restaurantData?.restaurantList || []).find(x => x._id === id)
    setSelectedRestaurant(r || null)
  }

  const nextAreaName = (len) => `Area ${len + 1}`

  // ── Commit a computed path as a new/edited boundary area ────────────────
  const commitZonePath = (path) => {
    if (modalMode === 'edit' && editingZoneIndex !== null) {
      setZones(prev => prev.map((z, i) => i === editingZoneIndex ? { ...z, _path: path, polygon: pathToPolygonInput(path) } : z))
      setSelectedZoneIndex(editingZoneIndex)
    } else {
      const newIndex = zones.length
      setZones(prev => [...prev, { name: nextAreaName(prev.length), fee: '0', sortOrder: prev.length, _path: path, polygon: pathToPolygonInput(path) }])
      setSelectedZoneIndex(newIndex)
    }
  }

  // ── Freehand draw-on-map ───────────────────────────────────────────────
  const handleDrawReady = (draw) => {
    drawRef.current = draw
    if (!draw) return
    if ((draw.getAll()?.features?.length || 0) > 0) return
    const existing = editingZoneIndex !== null ? zones[editingZoneIndex]?._path : null
    if (existing && existing.length >= 3) {
      const feature = pathToGeoJsonFeature(existing)
      const [id] = draw.add(feature)
      draw.changeMode('direct_select', { featureId: id })
    } else {
      draw.changeMode('draw_polygon')
    }
  }

  const syncDrawToZone = () => {
    const draw = drawRef.current
    if (!draw) return
    const feature = draw.getAll()?.features?.[0]
    if (!feature) return
    const path = drawFeatureToPath(feature)
    if (path.length < 3) return
    setZones(prev => prev.map((z, i) => i === editingZoneIndex ? { ...z, _path: path, polygon: pathToPolygonInput(path) } : z))
  }

  const handleDrawCreate = () => {
    const draw = drawRef.current
    if (!draw) return
    const feature = draw.getAll()?.features?.[0]
    if (!feature) return
    const path = drawFeatureToPath(feature)
    if (path.length < 3) return
    if (editingZoneIndex !== null) {
      syncDrawToZone()
    } else {
      const newIndex = zones.length
      setZones(prev => [...prev, { name: nextAreaName(prev.length), fee: '0', sortOrder: prev.length, _path: path, polygon: pathToPolygonInput(path) }])
      setSelectedZoneIndex(newIndex)
      setEditingZoneIndex(newIndex)
    }
    if (feature.id != null) draw.changeMode('direct_select', { featureId: feature.id })
  }

  const finishDrawing = () => {
    const draw = drawRef.current
    const feature = draw?.getAll()?.features?.[0]
    const drawnPath = feature ? drawFeatureToPath(feature) : null
    setZones(prev => {
      let next = prev
      if (drawnPath && drawnPath.length >= 3) {
        if (editingZoneIndex !== null && prev[editingZoneIndex]) {
          next = prev.map((z, i) => i === editingZoneIndex ? { ...z, _path: drawnPath, polygon: pathToPolygonInput(drawnPath) } : z)
        } else {
          next = [...prev, { name: nextAreaName(prev.length), fee: '0', sortOrder: prev.length, _path: drawnPath, polygon: pathToPolygonInput(drawnPath) }]
        }
      }
      persistZones(next)
      return next
    })
    setDrawActive(false)
    setEditingZoneIndex(null)
  }

  // Persist the boundary areas (fee is irrelevant for the boundary → always 0).
  const persistZones = (zoneList) => {
    if (!selectedRestaurantId) return
    const zonesInput = (zoneList || [])
      .filter(z => z._path && z._path.length >= 3)
      .map((z, idx) => ({ name: z.name?.trim() || nextAreaName(idx), fee: 0, polygon: pathToPolygonInput(z._path), sortOrder: idx }))
    mutSaveZones({ variables: { restaurantId: selectedRestaurantId, zones: zonesInput } })
  }

  // ── Boundary-area modal ─────────────────────────────────────────────────
  const openAddModal = () => {
    setDrawActive(false)
    setModalMode('add')
    setEditingZoneIndex(null)
    setModalMethod('draw')
    setModalDistanceMiles('5')
    setModalError('')
    setModalOpen(true)
  }

  const openEditModal = (index) => {
    setDrawActive(false)
    setModalMode('edit')
    setEditingZoneIndex(index)
    setModalMethod('draw')
    setModalDistanceMiles('5')
    setModalError('')
    setSelectedZoneIndex(index)
    setModalOpen(true)
  }

  const handleModalContinue = async () => {
    if (modalMethod === 'draw') {
      if (selectedRestaurant?.latitude && selectedRestaurant?.longitude) {
        setMapCenter({ lat: parseFloat(selectedRestaurant.latitude), lng: parseFloat(selectedRestaurant.longitude) })
      }
      setModalOpen(false)
      setDrawActive(true)
      return
    }
    // Driving distance: the area actually reachable along roads, from Mapbox.
    // A failure is shown rather than swapped for a circle — a circle is what
    // this replaced, and a wrong boundary silently accepts orders we can't
    // deliver.
    if (!selectedRestaurant?.latitude || !selectedRestaurant?.longitude) {
      setModalError('Restaurant location not available.'); return
    }
    const center = { lat: parseFloat(selectedRestaurant.latitude), lng: parseFloat(selectedRestaurant.longitude) }
    setModalBusy(true)
    setModalError('')
    try {
      const path = await drivingAreaPath(center, modalDistanceMiles, MAPBOX_TOKEN)
      commitZonePath(path)
      setModalOpen(false)
    } catch (e) {
      setModalError(e.message)
    } finally {
      setModalBusy(false)
    }
  }

  const handleDeleteZone = (index) => {
    setZones(prev => prev.filter((_, i) => i !== index))
    setSelectedZoneIndex(prev => (prev === null || prev === index) ? null : (prev > index ? prev - 1 : prev))
  }

  const handleNameChange = (index, value) =>
    setZones(prev => prev.map((z, i) => i === index ? { ...z, name: value } : z))

  const handleSave = () => {
    if (!selectedRestaurantId) return
    if (zones.some(z => !z._path || z._path.length < 3)) { setErrorMsg('A boundary area has fewer than 3 points.'); return }
    setErrorMsg('')
    persistZones(zones)
  }

  // ── Derived ──────────────────────────────────────────────────────────────
  const restaurants = restaurantData?.restaurantList || []
  const platformRestaurants = restaurants.filter(r => !selfDeliveryAllDay(r))
  const selfDeliveryRestaurants = restaurants.filter(r => r.selfDelivery === true)
  const summaryFor = (id) => (summaryData?.allRestaurantDeliverySummary || []).find(s => String(s.restaurantId) === String(id))

  const sectionCard = { borderRadius: 3, border: '1px solid #e8edf3', boxShadow: '0 1px 2px rgba(16,24,40,0.04)' }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Typography variant="h5" fontWeight={800} mb={0.5}>Delivery Boundaries</Typography>
      <Typography variant="body2" color="text.secondary" mb={2.5}>
        Control how customers are charged for delivery, and how far each restaurant delivers.
      </Typography>

      {/* Tabs */}
      <Paper variant="outlined" sx={{ ...sectionCard, mb: 2.5, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 1,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 56, fontSize: 14 },
            '& .MuiTabs-indicator': { height: 3, borderRadius: 3 },
          }}
        >
          <Tab icon={<RouteOutlinedIcon fontSize="small" />} iconPosition="start" label="Delivery fee" />
          <Tab icon={<MapOutlinedIcon fontSize="small" />} iconPosition="start" label="Delivery boundaries" />
          <Tab icon={<StorefrontOutlinedIcon fontSize="small" />} iconPosition="start" label="Self-delivery" />
        </Tabs>
      </Paper>

      {/* ── TAB 0: Delivery fee (global distance tiers) ── */}
      {tab === 0 && (
        <Paper variant="outlined" sx={{ ...sectionCard, p: { xs: 2, md: 3 } }}>
          <PlatformDistancePricingEditor />
        </Paper>
      )}

      {/* ── TAB 1: Delivery boundaries (per platform restaurant) ── */}
      {tab === 1 && (
        <Box>
          <Paper variant="outlined" sx={{ ...sectionCard, p: { xs: 2, md: 3 }, mb: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={0.5}>Delivery boundaries</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Set the maximum area each platform restaurant delivers to. A restaurant with no boundary
              can’t take delivery orders.
            </Typography>

            {/* Platform restaurants overview */}
            <TableContainer sx={{ borderRadius: 2, border: '1px solid #eef2f6' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell><b>Restaurant</b></TableCell>
                    <TableCell><b>Address</b></TableCell>
                    <TableCell><b>Boundary</b></TableCell>
                    <TableCell align="right"><b>Action</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {platformRestaurants.map(r => {
                    const hasBoundary = (summaryFor(r._id)?.zoneCount || 0) > 0
                    const truck = r.isMobile ? truckRadiusStatus(r) : null
                    const isSel = selectedRestaurantId === r._id
                    return (
                      <TableRow key={r._id} hover selected={isSel}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {r.name}
                          {r.isMobile && (
                            <Typography variant="caption" display="block" color="text.secondary">Food truck</Typography>
                          )}
                          {r.selfDelivery === true && (
                            <Typography variant="caption" display="block" color="text.secondary">{ownDriversNote(r)}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{r.address || '—'}</TableCell>
                        <TableCell>
                          {truck
                            ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: truck.ok ? 'success.main' : truck.ok === false ? 'warning.main' : 'text.secondary' }}>
                                {truck.ok ? <CheckCircleOutlineIcon fontSize="small" /> : truck.ok === false ? <WarningAmberIcon fontSize="small" /> : <StorefrontOutlinedIcon fontSize="small" />}
                                <Typography variant="caption" fontWeight={700}>{truck.text}</Typography>
                              </Box>
                            : hasBoundary
                            ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                                <CheckCircleOutlineIcon fontSize="small" /><Typography variant="caption" fontWeight={700}>Set</Typography>
                              </Box>
                            : <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'warning.main' }}>
                                <WarningAmberIcon fontSize="small" /><Typography variant="caption" fontWeight={700}>None — no delivery</Typography>
                              </Box>}
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" variant={isSel ? 'contained' : 'outlined'}
                            startIcon={r.isMobile ? <MapOutlinedIcon /> : <EditOutlinedIcon />}
                            onClick={() => handleRestaurantChange(r._id)}
                            sx={{ textTransform: 'none', fontSize: 12, borderRadius: 2 }}>
                            {r.isMobile ? (isSel ? 'Viewing' : 'View') : (isSel ? 'Editing' : 'Edit')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                  {!restaurantsLoading && platformRestaurants.length === 0 && (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>No platform restaurants.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2.5 }}>
              <FormControl size="small" sx={{ minWidth: 360 }}>
                <InputLabel>Select Restaurant</InputLabel>
                <Select value={selectedRestaurantId} label="Select Restaurant"
                  onChange={e => handleRestaurantChange(e.target.value)} disabled={restaurantsLoading}>
                  {platformRestaurants.map(r => (
                    <MenuItem key={r._id} value={r._id}>{r.name}{r.address ? ` — ${r.address}` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {!selectedRestaurantId && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>Select a restaurant above to set its delivery boundary.</Alert>
          )}

          {selectedRestaurantId && selectedRestaurant?.isMobile && (
            <Paper variant="outlined" sx={{ ...sectionCard, p: { xs: 1.5, md: 2 } }}>
              <TruckDeliveryToday restaurantId={selectedRestaurantId} />
            </Paper>
          )}

          {selectedRestaurantId && !selectedRestaurant?.isMobile && (
            <Paper variant="outlined" sx={{ ...sectionCard, p: { xs: 1.5, md: 2 } }}>
              <Box sx={{ display: 'flex', gap: 2, height: 560, flexDirection: { xs: 'column', md: 'row' } }}>
                {/* Boundary list */}
                <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>Boundary areas ({zones.length})</Typography>
                    <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={openAddModal}
                      variant="outlined" sx={{ textTransform: 'none', borderRadius: 2 }}>Add area</Button>
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  {zonesLoading && <CircularProgress size={22} sx={{ alignSelf: 'center', mt: 2 }} />}
                  <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {zones.length === 0 && !zonesLoading && (
                      <Typography variant="body2" color="text.secondary" mt={2} textAlign="center">
                        No boundary yet. Add an area (draw freehand, or a distance radius) so this
                        restaurant can take delivery orders.
                      </Typography>
                    )}
                    {zones.map((zone, i) => {
                      const color = ZONE_COLORS[i % ZONE_COLORS.length]
                      const isSel = selectedZoneIndex === i
                      return (
                        <Box key={i} onClick={() => setSelectedZoneIndex(i)}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, mb: 1, borderRadius: 2, cursor: 'pointer',
                            border: isSel ? `2px solid ${color}` : '2px solid transparent', backgroundColor: isSel ? `${color}14` : '#f8fafc' }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                          {isSel ? (
                            <TextField
                              value={zone.name}
                              onChange={e => handleNameChange(i, e.target.value)}
                              onClick={e => e.stopPropagation()}
                              size="small"
                              variant="standard"
                              placeholder="Area name"
                              sx={{ flex: 1, '& input': { fontWeight: 600, fontSize: '0.875rem', py: 0.25 } }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{zone.name}</Typography>
                          )}
                          <Tooltip title="Redraw / edit shape">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); openEditModal(i) }} sx={{ color: '#94a3b8', '&:hover': { color: '#4f46e5' } }}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete area">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); handleDeleteZone(i) }} sx={{ color: '#94a3b8', '&:hover': { color: '#d32f2f' } }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )
                    })}
                  </Box>
                  <Divider sx={{ mt: 1, mb: 1.5 }} />
                  {successMsg && <Alert severity="success" sx={{ mb: 1 }}>{successMsg}</Alert>}
                  {errorMsg && <Alert severity="error" sx={{ mb: 1 }}>{errorMsg}</Alert>}
                  <Button variant="contained" fullWidth onClick={handleSave} disabled={saving || zones.length === 0}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                    {saving ? 'Saving…' : 'Save boundary'}
                  </Button>
                </Box>

                {/* Map */}
                <Box sx={{ flex: 1, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0', position: 'relative', minHeight: 320 }}>
                  {drawActive && (
                    <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, display: 'flex', gap: 1, alignItems: 'center', bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 2, px: 1.5, py: 0.75, boxShadow: 2 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Click &amp; drag to draw · release to close</Typography>
                      <Button size="small" variant="contained" onClick={finishDrawing} sx={{ borderRadius: 2, textTransform: 'none' }}>Done drawing</Button>
                    </Box>
                  )}
                  <Map
                    ref={mapRef}
                    mapboxAccessToken={MAPBOX_TOKEN}
                    initialViewState={{ longitude: mapCenter.lng, latitude: mapCenter.lat, zoom: 12 }}
                    style={MAP_CONTAINER_STYLE}
                    mapStyle="mapbox://styles/mapbox/streets-v12"
                    onLoad={fitToContent}
                  >
                    {drawActive && (
                      <DrawControl position="top-left" freehand styles={DRAW_STYLES} displayControlsDefault={false}
                        controls={{ polygon: true, trash: true }} defaultMode="simple_select"
                        onReady={handleDrawReady} onCreate={handleDrawCreate} onUpdate={syncDrawToZone} onDelete={syncDrawToZone} />
                    )}
                    {selectedRestaurant?.latitude && selectedRestaurant?.longitude && (
                      <Marker longitude={parseFloat(selectedRestaurant.longitude)} latitude={parseFloat(selectedRestaurant.latitude)} anchor="center">
                        <div title={selectedRestaurant.name} style={{ width: 16, height: 16, borderRadius: '50%', background: '#D32F2F', border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }} />
                      </Marker>
                    )}
                    {zones.map((zone, i) => {
                      if (!zone._path || zone._path.length < 3) return null
                      if (drawActive && i === editingZoneIndex) return null
                      const color = ZONE_COLORS[i % ZONE_COLORS.length]
                      const isSel = selectedZoneIndex === i
                      const feature = pathToGeoJsonFeature(zone._path)
                      if (!feature) return null
                      return (
                        <Source key={i} id={`zone-${i}`} type="geojson" data={feature}>
                          <Layer id={`zone-fill-${i}`} type="fill" paint={{ 'fill-color': color, 'fill-opacity': isSel ? 0.25 : 0.12 }} />
                          <Layer id={`zone-line-${i}`} type="line" paint={{ 'line-color': color, 'line-width': isSel ? 3 : 1.5 }} />
                        </Source>
                      )
                    })}
                  </Map>
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {/* ── TAB 2: Self-delivery (read-only) ── */}
      {tab === 2 && (
        <Paper variant="outlined" sx={{ ...sectionCard, p: { xs: 2, md: 3 } }}>
          <Typography variant="subtitle1" fontWeight={700} mb={0.5}>Self-delivery restaurants</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            These deliver with their own drivers and set their own pricing &amp; zones on their vendor
            dashboard. They aren’t affected by platform pricing and can’t be edited here.
          </Typography>
          <TableContainer sx={{ borderRadius: 2, border: '1px solid #eef2f6' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell><b>Restaurant</b></TableCell>
                  <TableCell><b>Address</b></TableCell>
                  <TableCell><b>Vendor zones</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selfDeliveryRestaurants.map(r => {
                  const selfCount = summaryFor(r._id)?.selfDeliveryZoneCount || 0
                  return (
                    <TableRow key={r._id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{r.address || '—'}</TableCell>
                      <TableCell><Chip label={`${selfCount} zone${selfCount !== 1 ? 's' : ''}`} size="small" variant="outlined" /></TableCell>
                      <TableCell>
                        <Chip label="Self Delivery" size="small" sx={{ bgcolor: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', fontWeight: 700, fontSize: '0.7rem' }} />
                      </TableCell>
                    </TableRow>
                  )
                })}
                {summaryLoading && selfDeliveryRestaurants.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}><CircularProgress size={20} /></TableCell></TableRow>
                )}
                {!restaurantsLoading && selfDeliveryRestaurants.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>No self-delivery restaurants.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Add / Edit boundary-area modal ── */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {modalMode === 'edit' ? 'Edit boundary area' : 'Add boundary area'}
        </DialogTitle>
        <DialogContent>
          {modalError && <Alert severity="error" sx={{ mb: 2 }}>{modalError}</Alert>}
          <Typography variant="body2" color="text.secondary" mb={1.5}>How do you want to define the area?</Typography>
          <RadioGroup value={modalMethod} onChange={e => { setModalMethod(e.target.value); setModalError('') }}>
            <FormControlLabel value="draw" control={<Radio />} label="Draw on map (freehand)" />
            <FormControlLabel value="distance" control={<Radio />} label="Driving distance from restaurant" />
          </RadioGroup>

          {modalMethod === 'draw' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: 0.5 }}>
              The map switches to draw mode — click &amp; drag to trace the area, adjust vertices, then
              press <strong>Done drawing</strong>.
            </Typography>
          )}
          {modalMethod === 'distance' && (
            <Box sx={{ ml: 4, mt: 1 }}>
              <DrivingDistanceField
                value={modalDistanceMiles}
                onChange={v => { setModalDistanceMiles(v); setModalError('') }}
                disabled={modalBusy}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setModalOpen(false)} disabled={modalBusy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={handleModalContinue}
            variant="contained"
            disabled={modalBusy}
            startIcon={modalBusy ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, minWidth: 110 }}>
            {modalMethod === 'draw' ? 'Start drawing' : modalBusy ? 'Measuring roads…' : 'Add area'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
